import React, { useState, useEffect } from 'react';
import '../../assets/css/EmailCenter.css';
import EmailService from '../../services/EmailService';
import SmtpConfigService from '../../services/SmtpConfigService';
import ConnectEmailPage from './ConnectEmailPage';
import EmailInboxLayout from './EmailInboxLayout';

const buildId = () => `acc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const loadPersistedAccounts = async () => {
  const accounts = [];

  // Restore Gmail session — connection state lives server-side now, not a client token.
  const gmailStatus = await EmailService.getGmailConnectionStatus().catch(() => null);
  if (gmailStatus?.connected) {
    accounts.push({ id: buildId(), type: 'gmail', email: EmailService.getStoredGmailEmail(), label: 'Gmail' });
  }

  // Restore Zoho session — connection state lives server-side now, not a client token.
  const zohoStatus = await EmailService.getZohoConnectionStatus().catch(() => null);
  if (zohoStatus?.connected) {
    accounts.push({ id: buildId(), type: 'zoho', email: '', label: 'Zoho Mail' });
  }

  // Restore SMTP sessions stored in localStorage
  const smtpSessions = (() => {
    try { return JSON.parse(localStorage.getItem('smtp_sessions')) || []; } catch { return []; }
  })();
  smtpSessions.forEach((s) => {
    const config = SmtpConfigService.getConfigs().find((c) => c.id === s.configId);
    if (config) {
      accounts.push({ id: s.accountId, type: 'smtp', config, email: config.senderEmail, label: config.name });
    }
  });

  return accounts;
};

const persistSmtpSessions = (accounts) => {
  const sessions = accounts
    .filter((a) => a.type === 'smtp')
    .map((a) => ({ accountId: a.id, configId: a.config.id }));
  localStorage.setItem('smtp_sessions', JSON.stringify(sessions));
};

const EmailCenter = () => {
  const [accounts, setAccounts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showConnectPage, setShowConnectPage] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadPersistedAccounts().then((loadedAccounts) => {
      if (cancelled) return;
      setAccounts(loadedAccounts);
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  const handleAccountConnected = (accountData) => {
    const newAccount = { id: buildId(), ...accountData };

    if (accountData.type === 'smtp') {
      EmailService.setActiveSmtpProvider(accountData.email, accountData.config?.id);
    }

    setAccounts((prev) => {
      const filtered = prev.filter(
        (a) => !(a.type === newAccount.type && a.email === newAccount.email)
      );
      const updated = [...filtered, newAccount];
      persistSmtpSessions(updated);
      return updated;
    });
    setShowConnectPage(false);
  };

  const handleDisconnect = async (accountId) => {
    const account = accounts.find((a) => a.id === accountId);
    if (account?.type === 'gmail') {
      await EmailService.clearStoredEmailAuth('gmail');
    } else if (account?.type === 'zoho') {
      await EmailService.clearStoredEmailAuth('zoho');
    }

    setAccounts((prev) => {
      const updated = prev.filter((a) => a.id !== accountId);
      persistSmtpSessions(updated);
      return updated;
    });
  };

  if (!loaded) {
    return null;
  }

  if (accounts.length === 0 || showConnectPage) {
    return (
      <ConnectEmailPage
        onConnect={handleAccountConnected}
        onCancel={accounts.length > 0 ? () => setShowConnectPage(false) : null}
      />
    );
  }

  return (
    <EmailInboxLayout
      accounts={accounts}
      onAddAccount={() => setShowConnectPage(true)}
      onDisconnect={handleDisconnect}
    />
  );
};

export default EmailCenter;
